import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RevenueOpportunitiesSectionState } from 'anchor-scout-common/types';
import { useOverlayContext } from 'seagull';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useRevenueOpportunityDismissals } from '~/hooks/useRevenueOpportunityDismissals/useRevenueOpportunityDismissals';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { cardIdentity, describeCard, matchCard, opportunityBiData, partitionByRecognisedKind, resolveFrontIndex, unrecognisedKindReport } from '~/utils/revenueOpportunities/opportunityCard';
import { opportunityRoute } from '~/utils/revenueOpportunities/opportunityDestination';
import { dismissChoices } from '~/utils/revenueOpportunities/opportunityDismissals';
import { seeAllLink } from '~/utils/revenueOpportunities/seeAllLink';
import { sectionView, unrecognisedStateReport } from '../RevenueOpportunitiesSection.utils';
import { useRevenueOpportunitiesSummary } from './useRevenueOpportunitiesSummary';
import { useSectionPreferences } from './useSectionPreferences';
const NO_CARDS = [];
export const useRevenueOpportunitiesSection = (toastTestIds)=>{
    var _ref, _ref1, _ref2;
    const navigate = useNavigate();
    const { browser } = useIoContext();
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { showInfoToast } = useOverlayContext();
    const { summary, isLoading, isFetching, error, refresh, readSummaryState } = useRevenueOpportunitiesSummary({
        refreshOnHookMount: true
    });
    const { isDismissing, dismissCard } = useRevenueOpportunityDismissals(toastTestIds, {
        identities: ((_ref = summary === null || summary === void 0 ? void 0 : summary.cards) !== null && _ref !== void 0 ? _ref : NO_CARDS).map(cardIdentity),
        isFetching,
        read: ()=>{
            var _ref;
            var _latest_summary;
            const latest = readSummaryState();
            return {
                identities: ((_ref = (_latest_summary = latest.summary) === null || _latest_summary === void 0 ? void 0 : _latest_summary.cards) !== null && _ref !== void 0 ? _ref : NO_CARDS).map(cardIdentity),
                isFetching: latest.isFetching
            };
        }
    });
    const { isCollapsed, toggleCollapsed, isHiddenWhileDone, hideWhileDone, releaseHide } = useSectionPreferences();
    const [front, setFront] = useState({
        identity: null,
        index: 0
    });
    const { recognised: cards, unrecognised } = useMemo(()=>{
        var _ref;
        return partitionByRecognisedKind((_ref = summary === null || summary === void 0 ? void 0 : summary.cards) !== null && _ref !== void 0 ? _ref : NO_CARDS);
    }, [
        summary
    ]);
    const cardViews = useMemo(()=>cards.map((card)=>describeCard(card, texts)), [
        cards
    ]);
    const frontIndex = resolveFrontIndex(cardViews.map((view)=>view.identity), front);
    const frontCard = cards[frontIndex];
    const frontView = cardViews[frontIndex];
    const lastSyncedAt = (_ref1 = summary === null || summary === void 0 ? void 0 : summary.lastSyncedAt) !== null && _ref1 !== void 0 ? _ref1 : null;
    const view = sectionView({
        isLoading,
        hasFailed: error != null,
        summary,
        recognisedCardCount: cards.length,
        isHiddenWhileDone
    });
    const scoutState = summary === null || summary === void 0 ? void 0 : summary.state;
    const skippedReport = unrecognisedKindReport(unrecognised);
    const unknownStateReport = unrecognisedStateReport(scoutState);
    useEffect(()=>{
        if (scoutState != null && scoutState !== RevenueOpportunitiesSectionState.Done) {
            releaseHide();
        }
    }, [
        scoutState,
        releaseHide
    ]);
    useEffect(()=>{
        if (skippedReport != null) {
            browser.reportError(new Error(skippedReport));
        }
    }, [
        skippedReport
    ]);
    useEffect(()=>{
        if (unknownStateReport != null) {
            browser.reportError(new Error(unknownStateReport));
        }
    }, [
        unknownStateReport
    ]);
    function showCard(index) {
        setFront({
            identity: cardViews[index].identity,
            index
        });
    }
    function showNextCard() {
        showCard((frontIndex + 1) % cardViews.length);
    }
    function actOnFrontCard() {
        navigate(matchCard(frontCard, {
            single: (param)=>{
                let { opportunity } = param;
                return opportunityRoute(opportunity);
            },
            group: (param)=>{
                let { kind, serviceKey } = param;
                return seeAllLink({
                    kind,
                    serviceKey
                });
            }
        }));
    }
    function openSeeAll() {
        navigate(routes.revenueOpportunities);
    }
    function swipeDeck(direction) {
        if (direction === 'left') {
            showNextCard();
        }
    }
    function hideForNow() {
        hideWhileDone();
        showInfoToast(texts.section.toasts.hiddenForNow(), toastTestIds.HiddenForNow());
    }
    function retry() {
        void refresh({
            throwOnError: false
        });
    }
    function dismissFrontCard(option) {
        void dismissCard(frontCard, option, opportunityBiData(frontView, frontIndex));
    }
    return {
        view,
        retry,
        hideForNow,
        lastSyncedDate: lastSyncedAt == null ? null : timeModel.formatDate(lastSyncedAt, {
            long: true
        }),
        seeAllCount: (_ref2 = summary === null || summary === void 0 ? void 0 : summary.seeAllCount) !== null && _ref2 !== void 0 ? _ref2 : 0,
        openSeeAll,
        isCollapsed,
        toggleCollapsed,
        cardViews,
        frontIndex,
        showCard,
        showNextCard: cardViews.length > 1 ? showNextCard : undefined,
        actOnFrontCard,
        frontDismissOptions: frontCard ? dismissChoices(frontCard, texts) : [],
        isFrontDismissing: frontView != null && isDismissing(frontView.identity),
        dismissFrontCard,
        swipeDeck
    };
};
